import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../lib/api";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Truck, Star, ShieldCheck, Warning, MagnifyingGlass, Trophy, CheckCircle, Timer, Lightning, HandPalm } from "@phosphor-icons/react";

const getScoreColor = (score) => {
  if (score >= 80) return "text-emerald-600";
  if (score >= 60) return "text-amber-600";
  return "text-red-600";
};

const getScoreBadge = (score) => {
  if (score >= 90) return { label: "Excellent", cls: "bg-emerald-100 text-emerald-700" };
  if (score >= 75) return { label: "Good", cls: "bg-blue-100 text-blue-700" };
  if (score >= 60) return { label: "Average", cls: "bg-amber-100 text-amber-700" };
  return { label: "Needs Improvement", cls: "bg-red-100 text-red-700" };
};

export default function SupplierScorecardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    API.get("/reports/supplier-scorecard").then(({ data }) => setData(data)).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>;

  const scorecards = (data?.scorecards || []).filter(s =>
    !search || (s.company_name || "").toLowerCase().includes(search.toLowerCase()) || (s.trade || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="supplier-scorecard-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
          <Trophy size={18} className="text-white" weight="fill" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Supplier Performance</h1>
          <p className="text-xs text-muted-foreground">{data?.total || 0} suppliers ranked</p>
        </div>
      </div>

      <div className="relative max-w-sm mb-4">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search suppliers..." className="pl-9 h-9 text-sm" data-testid="scorecard-search" />
      </div>

      <div className="space-y-2">
        {scorecards.map((s, rank) => {
          const badge = getScoreBadge(s.overall_score);
          return (
            <div key={s.id} className="border border-border rounded-lg p-4 bg-card hover:shadow-sm transition-shadow cursor-pointer" onClick={() => navigate(`/vendors/${s.id}`)} data-testid={`scorecard-${s.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground shrink-0">
                    #{rank + 1}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-semibold">{s.company_name || s.contact_name}</p>
                      {s.trade && <Badge variant="outline" className="text-[9px]">{s.trade}</Badge>}
                      <Badge className={`text-[9px] ${badge.cls}`}>{badge.label}</Badge>
                      {!s.has_activity && <Badge variant="outline" className="text-[9px] text-muted-foreground" data-testid={`sc-nodata-${s.id}`}>No activity yet</Badge>}
                    </div>
                    <div className="flex items-center gap-4 text-[10px] text-muted-foreground mt-1">
                      <span className="flex items-center gap-1"><CheckCircle size={12} className="text-emerald-500" /> {s.completed_tasks}/{s.total_tasks} tasks ({s.completion_rate}%)</span>
                      <span className="flex items-center gap-1"><Warning size={12} className="text-red-500" /> {s.overdue_tasks} overdue</span>
                      <span className="flex items-center gap-1"><ShieldCheck size={12} className="text-blue-500" /> {s.valid_docs}/{s.total_docs} docs ({s.compliance_rate}%)</span>
                      <span className="flex items-center gap-1"><Truck size={12} /> {s.total_work_orders} WOs</span>
                      {s.avg_job_completion_days != null && <span className="flex items-center gap-1" data-testid={`sc-jobdays-${s.id}`}><Timer size={12} className="text-violet-500" /> avg job {s.avg_job_completion_days}d</span>}
                      {s.avg_quote_response_hours != null && <span className="flex items-center gap-1" data-testid={`sc-quoteresp-${s.id}`}><Lightning size={12} className="text-sky-500" /> quotes in {s.avg_quote_response_hours < 48 ? `${Math.round(s.avg_quote_response_hours)}h` : `${Math.round(s.avg_quote_response_hours / 24)}d`}</span>}
                      {s.acceptance_rate != null && <span className="flex items-center gap-1" data-testid={`sc-acceptance-${s.id}`}><HandPalm size={12} className="text-amber-500" /> accepts {s.acceptance_rate}%</span>}
                      {s.rating > 0 && <span className="flex items-center gap-0.5"><Star size={12} className="text-amber-500" weight="fill" /> {s.rating}</span>}
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className={`text-2xl font-bold font-[Chivo] ${getScoreColor(s.overall_score)}`}>{s.overall_score}</p>
                  <p className="text-[9px] text-muted-foreground">Score /100</p>
                </div>
              </div>
              {/* Score bars */}
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mt-3">
                <div>
                  <div className="flex items-center justify-between text-[9px] text-muted-foreground mb-0.5"><span>Completion</span><span>{s.completion_rate}%</span></div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${s.completion_rate}%` }} /></div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-[9px] text-muted-foreground mb-0.5"><span>Compliance</span><span>{s.compliance_rate}%</span></div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden"><div className="h-full bg-blue-500 rounded-full" style={{ width: `${s.compliance_rate}%` }} /></div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-[9px] text-muted-foreground mb-0.5"><span>Acceptance</span><span>{s.acceptance_rate != null ? `${s.acceptance_rate}%` : "—"}</span></div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden"><div className="h-full bg-amber-500 rounded-full" style={{ width: `${s.acceptance_rate || 0}%` }} /></div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-[9px] text-muted-foreground mb-0.5"><span>Quote Speed</span><span>{s.quote_speed_score != null ? `${s.quote_speed_score}%` : "—"}</span></div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden"><div className="h-full bg-sky-500 rounded-full" style={{ width: `${s.quote_speed_score || 0}%` }} /></div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-[9px] text-muted-foreground mb-0.5"><span>On-Time</span><span>{s.total_tasks > 0 ? Math.round(((s.total_tasks - s.overdue_tasks) / s.total_tasks) * 100) : 100}%</span></div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden"><div className="h-full bg-violet-500 rounded-full" style={{ width: `${s.total_tasks > 0 ? ((s.total_tasks - s.overdue_tasks) / s.total_tasks) * 100 : 100}%` }} /></div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
